import {
  User,
  Mail,
  Phone,
  Building2,
  Truck,
  Calendar,
  Activity,
  Package,
  Receipt,
  FileText,
  Ban,
  CheckCircle,
} from 'lucide-react';
import { useState } from 'react';
import { SidePanel } from '../ui/SidePanel';
import { Card } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { useAdminUser, useUpdateUserStatus, formatRelativeTime } from '../../api/hooks';
import { getUserStatus, type AdminUserResponse, type UserStatus } from '../../api/types';
import { cn } from '../../lib/cn';

interface UserDetailPanelProps {
  /** User ID to display, null if panel is closed */
  userId: string | null;
  /** Callback when panel should close */
  onClose: () => void;
}

function StatusBadge({ status }: { status: UserStatus }) {
  return (
    <span
      className={cn(
        'badge',
        status === 'active' && 'badge-active',
        status === 'inactive' && 'badge-suspended',
        status === 'new' && 'badge-pending'
      )}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  mono = false,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string | number | undefined | null;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon size={16} className="text-text-muted flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-caption text-text-muted">
          {label}
        </div>
        <div
          className={cn(
            'text-body-sm text-text truncate',
            mono && 'font-mono'
          )}
        >
          {value || '-'}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }: { label: string; value: number; icon: React.ComponentType<{ size?: number; className?: string }> }) {
  return (
    <div className="flex flex-col items-center p-3 bg-surface-variant rounded-card">
      <Icon size={20} className="text-text-muted mb-1" />
      <span className="text-title-md font-semibold text-text">
        {value}
      </span>
      <span className="text-caption text-text-muted">
        {label}
      </span>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="w-16 h-16 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    </div>
  );
}

function UserContent({ user }: { user: AdminUserResponse }) {
  const status = getUserStatus(user);
  const updateUserStatus = useUpdateUserStatus();
  const [showConfirm, setShowConfirm] = useState(false);

  const handleStatusChange = () => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    updateUserStatus.mutate(
      { userId: user.id, status: newStatus },
      {
        onSuccess: () => {
          setShowConfirm(false);
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* User Header */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-action-container flex items-center justify-center">
          <User size={32} className="text-action" />
        </div>
        <div className="flex-1">
          <h3 className="text-title-md font-semibold text-text">
            {user.name || 'No name'}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={status} />
            {user.email_verified && (
              <span className="badge badge-active">Verified</span>
            )}
          </div>
        </div>
      </div>

      {/* Status Actions */}
      {!showConfirm ? (
        <div className="flex gap-2">
          {user.status === 'active' ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowConfirm(true)}
              className="w-full"
            >
              <Ban size={16} />
              Suspend User
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowConfirm(true)}
              className="w-full"
            >
              <CheckCircle size={16} />
              Activate User
            </Button>
          )}
        </div>
      ) : (
        <Card padding="sm" hover={false}>
          <div className="space-y-3">
            <p className="text-body-sm text-text-secondary">
              {user.status === 'active'
                ? `Are you sure you want to suspend ${user.name}? They will lose access to the platform.`
                : `Are you sure you want to activate ${user.name}? They will regain access to the platform.`}
            </p>
            <div className="flex gap-2">
              <Button
                variant={user.status === 'active' ? 'destructive' : 'primary'}
                size="sm"
                onClick={handleStatusChange}
                loading={updateUserStatus.isPending}
                className="flex-1"
              >
                {user.status === 'active' ? 'Suspend' : 'Activate'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowConfirm(false)}
                disabled={updateUserStatus.isPending}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Loads" value={user.load_count} icon={Package} />
        <StatCard label="Expenses" value={user.expense_count} icon={Receipt} />
        <StatCard label="Invoices" value={user.invoice_count} icon={FileText} />
      </div>

      {/* Contact Info */}
      <Card padding="sm" hover={false}>
        <h4 className="text-label-sm font-medium text-text-secondary mb-2">
          Contact Information
        </h4>
        <DetailRow icon={Mail} label="Email" value={user.email} />
        <DetailRow icon={Phone} label="Phone" value={user.phone} />
      </Card>

      {/* Business Info */}
      {user.has_business && (
        <Card padding="sm" hover={false}>
          <h4 className="text-label-sm font-medium text-text-secondary mb-2">
            Business Information
          </h4>
          <DetailRow icon={Building2} label="Company" value={user.company_name} />
          <DetailRow icon={Truck} label="MC Number" value={user.mc_number} mono />
          <DetailRow icon={Truck} label="DOT Number" value={user.dot_number} mono />
          {user.ifta_base_state && (
            <DetailRow icon={Building2} label="IFTA Base State" value={user.ifta_base_state} />
          )}
        </Card>
      )}

      {/* Activity Info */}
      <Card padding="sm" hover={false}>
        <h4 className="text-label-sm font-medium text-text-secondary mb-2">
          Activity
        </h4>
        <DetailRow
          icon={Calendar}
          label="Joined"
          value={formatRelativeTime(user.created_at)}
        />
        <DetailRow
          icon={Activity}
          label="Last Active"
          value={user.last_active_at ? formatRelativeTime(user.last_active_at) : 'Never'}
        />
      </Card>

      {/* IDs (for debugging/admin) */}
      <Card padding="sm" hover={false}>
        <h4 className="text-label-sm font-medium text-text-secondary mb-2">
          System IDs
        </h4>
        <DetailRow icon={User} label="User ID" value={user.id} mono />
        <DetailRow icon={User} label="Cognito Sub" value={user.cognito_sub} mono />
      </Card>
    </div>
  );
}

/**
 * Side panel for displaying detailed user information.
 */
export function UserDetailPanel({ userId, onClose }: UserDetailPanelProps) {
  const { data: user, isLoading, error } = useAdminUser(userId || '', {
    enabled: !!userId,
  });

  return (
    <SidePanel
      open={!!userId}
      onClose={onClose}
      title="User Details"
      subtitle={user?.email}
      width="md"
    >
      {isLoading && <LoadingState />}
      {error && (
        <div className="text-loss text-body-sm">
          Failed to load user details. Please try again.
        </div>
      )}
      {user && <UserContent user={user} />}
    </SidePanel>
  );
}
